import React, { useState, useEffect, useMemo, useRef, useLayoutEffect, useCallback } from "react";
import { Card, Col, Table, Tabs, Tab, Modal, Form, Button, Row, Spinner, Badge, ProgressBar } from "react-bootstrap";
import { useNavigate, useLocation } from "react-router-dom";
import toast from "react-hot-toast";
import { Users, UserCheck, Clock, ShieldAlert, X, Calendar, MapPin, MessageSquare, Eye, CheckCircle, Info } from "lucide-react";
import EditPartner from "../Modals/EditPartner";
import ConfirmDeleteModal from "../Modals/ConfirmDelete";
import Pagination from "../components/Pagination";
import TableActionsMenu from "../components/TableActionsMenu";
import PartnerDashboard from "./PartnerDashboard";
import PartnerBilling from "./PartnerBilling";
import { getPartner, updatePartner, blockUser, getPackage, getPartnerStats, deleteParnter, assignPartnerPlan, getWhatsAppPackage, WhatsAppPackageAssign, getInactivePartnersNoLogin, getInactivePartnersNoWedding, getSUBSCRIPTION } from "../../services/UserServices/UserService";
import moment from "moment";
const PartnerStats = ({ data, loading }) => {
  const stats = [

    {
      title: "Interested Partners",
      count: data?.totalPendingPartners ?? data?.interested_partners ?? data?.pendingPartners ?? data?.pending_partners ?? data?.interested ?? data?.pending ?? 0,
      icon: Clock,
      color: "#d97706",
      bgLight: "rgba(217, 119, 6, 0.12)",
      subtext: "Pending approval",
    },
    {
      title: "Approved Partners",
      count: data?.totalActivePartners ?? data?.active_partners ?? data?.approvedPartners ?? data?.approved_partners ?? data?.active ?? data?.approved ?? 0,
      icon: UserCheck,
      color: "#059669",
      bgLight: "rgba(5, 150, 105, 0.12)",
      subtext: "Approved ",
    },
    {
      title: "Blocked Partners",
      count: data?.totalBlockedPartners ?? data?.blocked_partners ?? data?.disabledPartners ?? data?.disabled_partners ?? data?.blocked ?? data?.disabled ?? 0,
      icon: ShieldAlert,
      color: "#dc2626",
      bgLight: "rgba(220, 38, 38, 0.12)",
      subtext: "Disabled partners",
    },
  ];

  return (
    <>
      <style>{`
        .partner-stat-card,
        .partner-stat-card .card-body {
          border-radius: 1.5rem !important;
          background: #ffffff !important;
          background-color: #ffffff !important;
          box-shadow: 0 8px 24px rgba(0, 0, 0, 0.06) !important;
          border: none !important;
          overflow: hidden !important;
          transition: transform 0.2s ease, box-shadow 0.2s ease !important;
        }
        .partner-stat-card:hover {
          transform: translateY(-2px) !important;
          box-shadow: 0 12px 28px rgba(0, 0, 0, 0.09) !important;
        }
      `}</style>
      <Row className="g-3 mb-4">
        {stats.map((stat, index) => {
          const IconComponent = stat.icon;
          return (
            <Col lg={4} md={6} sm={6} key={index}>
              <div className="card mt-2 partner-stat-card h-100 bg-white" style={{ background: "#ffffff" }}>
                <Card.Body className="p-3 d-flex align-items-center justify-content-between bg-white" style={{ background: "#ffffff" }}>
                  <div>
                    <span className="text-uppercase fw-bold text-muted" style={{ fontSize: "0.72rem", letterSpacing: "0.05em" }}>
                      {stat.title}
                    </span>
                    <h3 className="fw-bold mb-1 mt-1" style={{ color: stat.color, fontSize: "1.65rem" }}>
                      {loading ? <Spinner animation="border" size="sm" style={{ color: stat.color }} /> : (stat.count ?? 0)}
                    </h3>
                    <span className="text-muted" style={{ fontSize: "0.75rem" }}>
                      {stat.subtext}
                    </span>
                  </div>
                  <div
                    className="d-flex align-items-center justify-content-center rounded-circle flex-shrink-0"
                    style={{
                      width: 52,
                      height: 52,
                      backgroundColor: stat.bgLight,
                      color: stat.color,
                    }}
                  >
                    <IconComponent size={24} />
                  </div>
                </Card.Body>
              </div>
            </Col>
          );
        })}
      </Row>
    </>
  );
};

const AlwaysVisibleScrollbar = ({ targetRef }) => {
  const [scrollState, setScrollState] = useState({ scrollLeft: 0, maxScrollLeft: 0, clientWidth: 1, scrollWidth: 1 });

  const updateMetrics = useCallback(() => {
    const el = targetRef.current;
    if (!el) return;
    const maxScroll = Math.max(0, el.scrollWidth - el.clientWidth);
    setScrollState({
      scrollLeft: el.scrollLeft,
      maxScrollLeft: maxScroll,
      clientWidth: el.clientWidth || 1,
      scrollWidth: el.scrollWidth || 1,
    });
  }, [targetRef]);

  useEffect(() => {
    const el = targetRef.current;
    if (!el) return;

    updateMetrics();

    const handleScroll = () => {
      setScrollState((prev) => ({
        ...prev,
        scrollLeft: el.scrollLeft,
      }));
    };

    el.addEventListener("scroll", handleScroll, { passive: true });
    window.addEventListener("resize", updateMetrics);

    const ro = new ResizeObserver(updateMetrics);
    ro.observe(el);

    return () => {
      el.removeEventListener("scroll", handleScroll);
      window.removeEventListener("resize", updateMetrics);
      ro.disconnect();
    };
  }, [targetRef, updateMetrics]);

  const { scrollLeft, maxScrollLeft, clientWidth, scrollWidth } = scrollState;

  const visibleRatio = scrollWidth > 0 ? clientWidth / scrollWidth : 1;
  const thumbWidthPct = Math.min(80, Math.max(15, visibleRatio * 100));
  const maxThumbLeftPct = 100 - thumbWidthPct;
  const thumbLeftPct = maxScrollLeft > 0 ? (scrollLeft / maxScrollLeft) * maxThumbLeftPct : 0;

  const handleTrackClick = (e) => {
    const el = targetRef.current;
    if (!el || maxScrollLeft <= 0) return;
    const rect = e.currentTarget.getBoundingClientRect();
    const clickX = e.clientX - rect.left;
    const clickRatio = clickX / rect.width;
    el.scrollLeft = clickRatio * maxScrollLeft;
  };

  const handleThumbMouseDown = (e) => {
    e.preventDefault();
    e.stopPropagation();
    const el = targetRef.current;
    if (!el || maxScrollLeft <= 0) return;

    const startX = e.clientX;
    const startScrollLeft = el.scrollLeft;
    const trackWidth = e.currentTarget.parentElement.clientWidth;
    const thumbWidth = e.currentTarget.clientWidth;
    const scrollableTrackWidth = trackWidth - thumbWidth;

    if (scrollableTrackWidth <= 0) return;

    const onMouseMove = (moveEvent) => {
      const deltaX = moveEvent.clientX - startX;
      const scrollRatio = deltaX / scrollableTrackWidth;
      el.scrollLeft = startScrollLeft + scrollRatio * maxScrollLeft;
    };

    const onMouseUp = () => {
      window.removeEventListener("mousemove", onMouseMove);
      window.removeEventListener("mouseup", onMouseUp);
    };

    window.addEventListener("mousemove", onMouseMove);
    window.addEventListener("mouseup", onMouseUp);
  };

  if (maxScrollLeft <= 0) return null;

  return (
    <div
      className="always-visible-scrollbar-wrapper px-3 py-2 border-top"
      style={{
        backgroundColor: "#f8fafc",
        borderTop: "1px solid #e2e8f0",
      }}
    >
      <div
        className="always-visible-scrollbar-track"
        onClick={handleTrackClick}
        style={{
          width: "100%",
          height: "12px",
          backgroundColor: "#e2e8f0",
          borderRadius: "6px",
          position: "relative",
          cursor: "pointer",
          userSelect: "none",
        }}
      >
        <div
          className="always-visible-scrollbar-thumb"
          onMouseDown={handleThumbMouseDown}
          style={{
            position: "absolute",
            top: 0,
            left: `${thumbLeftPct}%`,
            width: `${thumbWidthPct}%`,
            height: "100%",
            backgroundColor: "#A60000",
            borderRadius: "6px",
            boxShadow: "0 1px 3px rgba(166, 0, 0, 0.4)",
            cursor: "grab",
          }}
        />
      </div>
    </div>
  );
};



export default function Partners({ defaultTab }) {
  const navigate = useNavigate();
  const location = useLocation();

  const [mainTab, setMainTab] = useState(() => {
    return defaultTab || location.state?.mainTab || sessionStorage.getItem("partners_mainTab") || "partners";
  });

  useEffect(() => {
    if (defaultTab) {
      setMainTab(defaultTab);
    }
  }, [defaultTab]);

  const [activeTab, setActiveTab] = useState(() => {
    return location.state?.activeTab || sessionStorage.getItem("partners_activeTab") || "interested";
  });
  const [search, setSearch] = useState("");
  const [filterDate, setFilterDate] = useState("");
  const [endDate, setEndDate] = useState("");
  const [packageFilter, setPackageFilter] = useState("");
  const [sortStatus, setSortStatus] = useState("");
  const [sortField, setSortField] = useState(() => {
    const tab = location.state?.activeTab || sessionStorage.getItem("partners_activeTab") || "interested";
    return tab === "interested" ? "createdAt" : "partnerData.approvedAt";
  });
  const [sortSubscriptionId, setSortSubscriptionId] = useState("");
  const [sortOrder, setSortOrder] = useState("desc");
  const [individualPackages, setIndividualPackages] = useState([]);
  const [combinedPackages, setCombinedPackages] = useState([]);

  // Lists state
  const [interestedPartners, setInterestedPartners] = useState([]);
  const [activePartners, setActivePartners] = useState([]);
  const [archivedPartners, setArchivedPartners] = useState([]);
  const [inactiveVendors, setInactiveVendors] = useState([]);
  const [inactiveType, setInactiveType] = useState("noLogin");
  const [loader, setLoader] = useState(false);
  const [totalItems, setTotalItems] = useState(0);

  const scrollPosRef = useRef(0);
  const tableScrollRef = useRef(null);

  // Pagination state
  const [currentPage, setCurrentPage] = useState(() => {
    const pageVal = location.state?.currentPage ?? sessionStorage.getItem("partners_currentPage");
    return pageVal !== null && pageVal !== undefined ? Number(pageVal) : 0;
  });
  const itemsPerPage = 10;

  useEffect(() => {
    if (activeTab) {
      sessionStorage.setItem("partners_activeTab", activeTab);
    }
  }, [activeTab]);

  useEffect(() => {
    sessionStorage.setItem("partners_currentPage", currentPage);
  }, [currentPage]);

  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [deletePartnerId, setDeletePartnerId] = useState(null);

  // Block/Unblock (Enable/Disable) states
  const [showBlockModal, setShowBlockModal] = useState(false);
  const [targetPartner, setTargetPartner] = useState(null);
  const [blockLoading, setBlockLoading] = useState(false);

  const handleToggleBlockClick = (partner) => {
    setTargetPartner(partner);
    setShowBlockModal(true);
  };

  const handleConfirmToggleBlock = async () => {
    if (!targetPartner) return;
    const partnerId = targetPartner._id || targetPartner.id;
    const nextBlockStatus = !targetPartner.isBlocked;
    setBlockLoading(true);
    try {
      await blockUser({ userId: partnerId, isBlocked: nextBlockStatus });
      toast.success(nextBlockStatus ? "Partner disabled successfully" : "Partner enabled successfully");
      setShowBlockModal(false);
      setTargetPartner(null);
      fetchPartners();
      fetchStats();
    } catch (error) {
      console.error("Error toggling partner status:", error);
      toast.error(error?.response?.data?.message || "Failed to update partner status");
    } finally {
      setBlockLoading(false);
    }
  };

  // Assign Package Modal state
  const [showPackageModal, setShowPackageModal] = useState(false);
  const [assignTargetPartner, setAssignTargetPartner] = useState(null);
  const [selectedAssignPackageId, setSelectedAssignPackageId] = useState("");
  const [receivedMoney, setReceivedMoney] = useState("");
  const [assignPackageLoading, setAssignPackageLoading] = useState(false);

  const selectedPackageObj = useMemo(() => {
    return individualPackages.find((pkg) => (pkg._id || pkg.id) === selectedAssignPackageId);
  }, [individualPackages, selectedAssignPackageId]);

  const handleOpenAssignPackageModal = (partner) => {
    setAssignTargetPartner(partner);
    const subId =
      partner?.subscriptionId ||
      partner?.partnerData?.subscriptionId ||
      partner?.plan?._id ||
      partner?.plan?.id ||
      "";
    setSelectedAssignPackageId(subId);

    const initialPkg = individualPackages.find((pkg) => (pkg._id || pkg.id) === subId);
    const initPrice = initialPkg?.price !== undefined && initialPkg?.price !== null
      ? String(initialPkg.price)
      : (partner?.receivedMoney || partner?.amountPaid || "");
    setReceivedMoney(initPrice);
    setShowPackageModal(true);
  };

  const handleConfirmAssignPackage = async () => {
    if (!assignTargetPartner) return;
    if (!selectedAssignPackageId) {
      toast.error("Please select a package to assign");
      return;
    }
    const partnerId = assignTargetPartner._id || assignTargetPartner.id;
    try {
      setAssignPackageLoading(true);
      await assignPartnerPlan({
        partnerId: partnerId,
        subscriptionId: selectedAssignPackageId,
        receiverAmount: Number(receivedMoney) || 0,
      });
      toast.success("Package assigned successfully!");
      setShowPackageModal(false);
      setAssignTargetPartner(null);
      setSelectedAssignPackageId("");
      setReceivedMoney("");
      fetchPartners();
      fetchStats();
    } catch (err) {
      console.error("Error assigning package:", err);
      toast.error(err?.response?.data?.message || "Failed to assign package");
    } finally {
      setAssignPackageLoading(false);
    }
  };
  // Assign WhatsApp Package Modal state
  const [showWaModal, setShowWaModal] = useState(false);
  const [assignWaPartner, setAssignWaPartner] = useState(null);
  const [selectedWaPlanId, setSelectedWaPlanId] = useState("");
  const [waReceivedMoney, setWaReceivedMoney] = useState("");
  const [assignWaLoading, setAssignWaLoading] = useState(false);
  const [whatsAppPackagesList, setWhatsAppPackagesList] = useState([]);

  const fetchWaPackages = async () => {
    try {
      const res = await getWhatsAppPackage();
      const resData = res?.data?.data || res?.data || {};
      const list = resData?.plans || resData?.docs || resData?.list || (Array.isArray(list) ? list : []);
      if (Array.isArray(list)) setWhatsAppPackagesList(list);
    } catch (err) {
      console.error("Error fetching WhatsApp packages:", err);
    }
  };

  const handleOpenAssignWaModal = (partner) => {
    setAssignWaPartner(partner);
    setSelectedWaPlanId("");
    setWaReceivedMoney("");
    fetchWaPackages();
    setShowWaModal(true);
  };

  const handleConfirmAssignWaPackage = async () => {
    if (!selectedWaPlanId) {
      toast.error("Please select a WhatsApp package to assign");
      return;
    }
    const partnerId = assignWaPartner?._id || assignWaPartner?.id;
    if (!partnerId) {
      toast.error("Partner ID not found");
      return;
    }
    try {
      setAssignWaLoading(true);
      await WhatsAppPackageAssign({
        partnerId: partnerId,
        whatsappPlanId: selectedWaPlanId,
        receiverAmount: Number(waReceivedMoney) || 0,
      });
      toast.success("WhatsApp package assigned successfully!");
      setShowWaModal(false);
      setAssignWaPartner(null);
      setSelectedWaPlanId("");
      setWaReceivedMoney("");
      fetchPartners();
      fetchStats();
    } catch (err) {
      console.error("Error assigning WhatsApp package:", err);
      toast.error(err?.response?.data?.message || "Failed to assign WhatsApp package");
    } finally {
      setAssignWaLoading(false);
    }
  };


  const [allSubscriptions, setAllSubscriptions] = useState([]);

  useEffect(() => {
    if (mainTab !== "partners") return;
    getSUBSCRIPTION()
      .then((res) => {
        const subData = res?.data?.data || res?.data;
        if (Array.isArray(subData)) {
          setAllSubscriptions(subData);
        }
      })
      .catch((err) => console.error("Error fetching subscriptions:", err));
  }, [mainTab]);

  useEffect(() => {
    if (mainTab !== "partners") return;
    getPackage(false)
      .then((res) => {
        const indData = res?.data?.data || res?.data;
        if (Array.isArray(indData)) setIndividualPackages(indData);
      })
      .catch((err) => console.error("Error fetching packages:", err));
  }, [mainTab]);
  const [statsPartner, setStatsPartner] = useState(null);
  const [statsLoading, setStatsLoading] = useState(false);

  const fetchStats = async () => {
    try {
      setStatsLoading(true);
      const res = await getPartnerStats();
      const data = res?.data?.data || res?.data;
      setStatsPartner(data);
    } catch (error) {
      console.error("Error fetching partner stats:", error);
    } finally {
      setStatsLoading(false);
    }
  };

  useEffect(() => {
    if (mainTab !== "partners") return;
    fetchStats();
  }, [mainTab]);

  const fetchPartners = async () => {
    try {
      setLoader(true);
      if (activeTab === "inactive") {
        const payload = {
          page: currentPage,
          limit: itemsPerPage,
          ...(search && { search }),
          ...(filterDate && { startDate: filterDate }),
          ...(endDate && { endDate }),
          ...(sortField && {
            sortBy: {
              field: sortField,
              order: sortOrder || "asc",
              ...(sortSubscriptionId && { subscriptionId: sortSubscriptionId }),
            },
          }),
        };

        const response = inactiveType === "noWedding"
          ? await getInactivePartnersNoWedding(payload)
          : await getInactivePartnersNoLogin(payload);
        const resData = response?.data?.data || response?.data;
        const list = resData?.partners || resData?.docs || resData?.list || resData?.inactivePartners || resData?.vendors || (Array.isArray(resData) ? resData : null);
        const count = resData?.count || resData?.total || resData?.totalCount || (list ? list.length : 0);

        if (list && Array.isArray(list)) {
          const processed = list.map((partner) => {
            const plans = partner?.partnerPlans || partner?.combinedPackages || partner?.plans || [];
            let essential = null;
            let premium = null;
            let elite = null;

            if (Array.isArray(plans)) {
              plans.forEach((p) => {
                const name = (
                  p?.subscriptionId?.name ||
                  p?.planId?.name ||
                  p?.packageId?.name ||
                  p?.name ||
                  ""
                ).toString().trim().toLowerCase();

                if (name === "essential") essential = p;
                else if (name === "premium") premium = p;
                else if (name === "elite") elite = p;
              });
            }

            return {
              ...partner,
              essentialPlan: essential,
              premiumPlan: premium,
              elitePlan: elite,
            };
          });
          setInactiveVendors(processed);
          setTotalItems(count);
        } else if (Array.isArray(resData)) {
          setInactiveVendors(resData);
          setTotalItems(resData.length);
        } else {
          setInactiveVendors([]);
          setTotalItems(0);
        }
        return;
      }

      const isArchiveTab = activeTab === "archive" || activeTab === "archived";
      const status = activeTab === "interested" ? "pending" : "accepted";
      const payload = {
        page: currentPage,
        limit: itemsPerPage,
        adminApproval: status,
        ...(isArchiveTab && { isBlocked: true }),
        ...(search && { search }),
        ...(packageFilter && { package: packageFilter }),
        ...(filterDate && { startDate: filterDate }),
        ...(endDate && { endDate }),
        ...(sortStatus && { status: sortStatus }),
        sortBy: sortField ? {
          field: sortField,
          order: sortOrder || "desc",
          ...(sortSubscriptionId && { subscriptionId: sortSubscriptionId }),
        } : {
          field: activeTab === "interested" ? "createdAt" : "partnerData.approvedAt",
          order: "desc",
        },
      };

      const response = await getPartner(payload);

      const resData = response?.data?.data || response?.data;
      const list = resData?.partners || resData?.docs || resData?.list || (Array.isArray(resData) ? resData : null);
      const count = resData?.count || resData?.total || resData?.totalCount || (list ? list.length : 0);

      if (list && Array.isArray(list)) {
        const processed = list.map((partner) => {
          const plans = partner?.partnerPlans || partner?.combinedPackages || partner?.plans || [];
          let essential = null;
          let premium = null;
          let elite = null;

          if (Array.isArray(plans)) {
            plans.forEach((p) => {
              const name = (
                p?.subscriptionId?.name ||
                p?.planId?.name ||
                p?.packageId?.name ||
                p?.name ||
                ""
              ).toString().trim().toLowerCase();

              if (name === "essential") essential = p;
              else if (name === "premium") premium = p;
              else if (name === "elite") elite = p;
            });
          }

          return {
            ...partner,
            essentialPlan: essential,
            premiumPlan: premium,
            elitePlan: elite,
          };
        });

        if (activeTab === "interested") {
          console.log("list", processed);
          setInterestedPartners(processed);
        } else if (isArchiveTab) {
          setArchivedPartners(processed);
        } else {
          setActivePartners(processed);
        }
        setTotalItems(count);
      }
    } catch (error) {
      console.error("Error fetching partners:", error);
    } finally {
      setLoader(false);
    }
  };


  useEffect(() => {
    if (mainTab !== "partners") return;
    fetchPartners();
  }, [mainTab, activeTab, currentPage, search, packageFilter, filterDate, endDate, sortStatus, sortField, sortOrder, inactiveType]);

  useEffect(() => {
    setCurrentPage(0);
  }, [search, filterDate, endDate, packageFilter, sortStatus, sortField, sortOrder, inactiveType]);

  const getValueByPath = (obj, path) => {
    if (!obj || !path) return "";
    if (path === "partnerPlans.remainingHostLimit") {
      const plans = obj?.partnerPlans || obj?.combinedPackages || obj?.plans || [];
      if (Array.isArray(plans) && plans.length > 0) {
        const limits = plans.map(p => p?.remainingHostLimit).filter(v => v !== undefined && v !== null);
        if (limits.length > 0) return Math.max(...limits);
      }
      return obj?.essentialPlan?.remainingHostLimit ?? obj?.premiumPlan?.remainingHostLimit ?? obj?.elitePlan?.remainingHostLimit ?? "";
    }
    if (path === "partnerPlans.totalHostLimit") {
      const plans = obj?.partnerPlans || obj?.combinedPackages || obj?.plans || [];
      if (Array.isArray(plans) && plans.length > 0) {
        const limits = plans.map(p => p?.totalHostLimit ?? p?.hostLimit).filter(v => v !== undefined && v !== null);
        if (limits.length > 0) return Math.max(...limits);
      }
      return obj?.essentialPlan?.totalHostLimit ?? obj?.premiumPlan?.totalHostLimit ?? obj?.elitePlan?.totalHostLimit ?? "";
    }
    const parts = path.split(".");
    let curr = obj;
    for (let part of parts) {
      if (curr === null || curr === undefined) return "";
      curr = curr[part];
    }
    return curr ?? "";
  };

  const currentList =
    activeTab === "interested"
      ? interestedPartners
      : activeTab === "archive" || activeTab === "archived"
        ? archivedPartners
        : activeTab === "inactive"
          ? inactiveVendors
          : activePartners;

  const paginatedList = Array.isArray(currentList) ? currentList : [];

  const dynamicPlans = useMemo(() => {
    if (Array.isArray(allSubscriptions) && allSubscriptions.length > 0) {
      return allSubscriptions;
    }
    if (Array.isArray(individualPackages) && individualPackages.length > 0) {
      return individualPackages;
    }
    const map = new Map();
    if (Array.isArray(paginatedList)) {
      paginatedList.forEach((partner) => {
        const plans = partner?.partnerPlans || partner?.combinedPackages || partner?.plans || [];
        if (Array.isArray(plans)) {
          plans.forEach((p) => {
            const id = p?.subscriptionId?._id || p?.subscriptionId || p?.planId?._id || p?.planId || p?._id || p?.id;
            const name = p?.subscriptionId?.name || p?.planId?.name || p?.packageId?.name || p?.name;
            if (name && id && !map.has(id.toString())) {
              map.set(id.toString(), { _id: id, name });
            }
          });
        }
      });
    }
    return Array.from(map.values());
  }, [allSubscriptions, individualPackages, paginatedList]);

  const renderPlanHeaders = (isInactive = false) => {
    if (!dynamicPlans || dynamicPlans.length === 0) return null;
    return dynamicPlans.map((plan) => {
      const planId = plan._id || plan.id || plan.name;
      const planName = plan.name || plan.title || "Plan";
      return (
        <React.Fragment key={planId}>
          <th
            className="cursor-pointer"
            onClick={() => handleHeaderSort("partnerPlans.totalHostLimit", planId)}
          >
            <div><strong>{planName}</strong></div>
            <small className="text-muted fw-semibold">
              {isInactive ? "Total" : "Consumed"}{" "}
              {sortField === "partnerPlans.totalHostLimit" && sortSubscriptionId === planId
                ? sortOrder === "asc"
                  ? "↑"
                  : "↓"
                : "↕"}
            </small>
          </th>
          <th
            className="cursor-pointer"
            onClick={() => handleHeaderSort("partnerPlans.remainingHostLimit", planId)}
          >
            <div><strong>{planName}</strong></div>
            <small className="text-muted fw-semibold">
              Remaining{" "}
              {sortField === "partnerPlans.remainingHostLimit" && sortSubscriptionId === planId
                ? sortOrder === "asc"
                  ? "↑"
                  : "↓"
                : "↕"}
            </small>
          </th>
        </React.Fragment>
      );
    });
  };

  const renderPlanCells = (partner) => {
    if (!dynamicPlans || dynamicPlans.length === 0) return null;
    const partnerPlansList = partner?.partnerPlans || partner?.combinedPackages || partner?.plans || [];
    return dynamicPlans.map((plan) => {
      const planId = plan._id || plan.id;
      const targetName = (plan.name || plan.title || "").toString().trim().toLowerCase();

      const matchedPlan = Array.isArray(partnerPlansList)
        ? partnerPlansList.find((p) => {
          const pId = p?.subscriptionId?._id || p?.subscriptionId || p?.planId?._id || p?.planId || p?._id || p?.id;
          const pName = (p?.subscriptionId?.name || p?.planId?.name || p?.packageId?.name || p?.name || "").toString().trim().toLowerCase();
          return (planId && pId && pId.toString() === planId.toString()) || (targetName && pName === targetName);
        })
        : null;

      return (
        <React.Fragment key={planId || plan.name}>
          <td>
            {matchedPlan
              ? `${(matchedPlan.totalHostLimit ?? matchedPlan.hostLimit ?? 0) - (matchedPlan.remainingHostLimit ?? 0)} / ${matchedPlan.totalHostLimit ?? matchedPlan.hostLimit ?? "-"}`
              : "-"}
          </td>
          <td>
            {matchedPlan ? (matchedPlan.remainingHostLimit ?? "-") : "-"}
          </td>
        </React.Fragment>
      );
    });
  };

  useLayoutEffect(() => {
    if (tableScrollRef.current && scrollPosRef.current > 0) {
      tableScrollRef.current.scrollLeft = scrollPosRef.current;
    }
  }, [paginatedList, loader, activeTab]);

  useEffect(() => {
    scrollPosRef.current = 0;
  }, [activeTab]);

  const totalCount = totalItems > 0 ? totalItems : (currentList ? currentList.length : 0);
  const totalPages = Math.ceil(totalCount / itemsPerPage) || 1;

  const partnersPagination = (
    <Pagination
      pageCount={totalPages}
      pageValue={currentPage}
      setPage={setCurrentPage}
      total={totalCount}
      pageSize={itemsPerPage}
    />
  );

  const handleHeaderSort = (field, subscriptionId = "") => {
    if (tableScrollRef.current) {
      scrollPosRef.current = tableScrollRef.current.scrollLeft;
    }
    if (sortField === field && sortSubscriptionId === subscriptionId) {
      setSortOrder((prev) => (prev === "asc" ? "desc" : "asc"));
    } else {
      setSortField(field);
      setSortSubscriptionId(subscriptionId);
      setSortOrder("desc");
    }
  };

  const handleDeletePartner = async () => {
    await deleteParnter(deletePartnerId)
    setShowDeleteModal(false);
    setDeletePartnerId(null);
    toast.success("Partner deleted successfully!");
    fetchStats();
    fetchPartners();
  };

  const todayDateObj = new Date();
  const todayStr = `${todayDateObj.getFullYear()}-${String(todayDateObj.getMonth() + 1).padStart(2, "0")}-${String(todayDateObj.getDate()).padStart(2, "0")}`;

  const handleStartDateChange = (e) => {
    const val = e.target.value;
    if (val && val > todayStr) {
      toast.error("Start date cannot be greater than current date");
      return;
    }
    if (val && endDate && val > endDate) {
      toast.error("Start date cannot be greater than end date");
      setEndDate("");
    }
    setFilterDate(val);
  };

  const handleEndDateChange = (e) => {
    const val = e.target.value;
    if (val && val > todayStr) {
      toast.error("End date cannot be greater than current date");
      return;
    }
    if (val && filterDate && val < filterDate) {
      toast.error("End date cannot be less than start date");
      return;
    }
    setEndDate(val);
  };

  if (mainTab === "dashboard") {
    return <PartnerDashboard />;
  }

  if (mainTab === "billing") {
    return <PartnerBilling />;
  }


  const defaultSortField = activeTab === "interested" ? "createdAt" : "partnerData.approvedAt";
  const hasActiveFilters = Boolean(filterDate || endDate || packageFilter || sortStatus || search || (sortField && sortField !== defaultSortField));

  return (
    <div className="qx-page qx-partners-page">

      <PartnerStats data={statsPartner} loading={statsLoading} />

      <Card className="qx-filter-card p-3 mb-3 border-0">
        <div className="d-flex flex-wrap align-items-center gap-2">

          <div className="position-relative flex-grow-1" style={{ maxWidth: "420px", minWidth: "220px" }}>
            <input
              type="text"
              className="form-control"
              placeholder="Search partner by name, phone..."
              value={search}
              onChange={(e) => setSearch(e.target.value)}
            />
            {search && (
              <button
                type="button"
                onClick={() => setSearch("")}
                className="btn p-0 position-absolute top-50 end-0 translate-middle-y me-2 border-0 bg-transparent"
                style={{ cursor: "pointer", color: "#667085" }}
              >
                <X size={14} />
              </button>
            )}
          </div>



          {/* Start Date */}
          <div style={{ maxWidth: "150px", minWidth: "130px" }}>
            <Form.Control
              type="date"
              value={filterDate}
              max={todayStr}
              onChange={handleStartDateChange}
              title="Start Date"
            />
          </div>

          {/* End Date */}
          <div style={{ maxWidth: "150px", minWidth: "130px" }}>
            <Form.Control
              type="date"
              value={endDate}
              min={filterDate || undefined}
              max={todayStr}
              onChange={handleEndDateChange}
              title="End Date"
            />
          </div>
          {hasActiveFilters && (
            <Button
              variant="outline-danger"
              size="sm"
              className="d-flex align-items-center gap-1 px-2 fw-bold rounded-pill"
              style={{ height: "34px", fontSize: "0.75rem", borderColor: "#800001", color: "#800001" }}
              onClick={() => {
                setFilterDate("");
                setEndDate("");
                setSearch("");
                setPackageFilter("");
                setSortStatus("");
                setSortField(activeTab === "interested" ? "createdAt" : "partnerData.approvedAt");
                setSortSubscriptionId("");
                setSortOrder("desc");
                setCurrentPage(0);
              }}
            >
              <X size={13} /> Clear
            </Button>
          )}
        </div>
      </Card>

      <Tabs
        id="controlled-tab-example"
        activeKey={activeTab}
        onSelect={(k) => {
          setActiveTab(k);
          setSearch("");
          setFilterDate("");
          setEndDate("");
          setPackageFilter("");
          setSortStatus("");
          setSortField(k === "interested" ? "createdAt" : "partnerData.approvedAt");
          setSortSubscriptionId("");
          setSortOrder("desc");
          setCurrentPage(0);
        }}
        className="mb-3 qx-partners-tabs"
      >

        <Tab eventKey="interested" title="Interested Partners">
          <div className="d-flex justify-content-between align-items-center mb-3 flex-wrap gap-2">
            <small className="qx-info-note">
              <Info size={14} />
              Partners that registered from the app
            </small>
            <Button
              variant="primary"
              style={{ background: "#A60000", color: "#FFB101" }}
              onClick={() => navigate("/create-partner", { state: { activeTab, currentPage } })}
            >
              + Create Partner
            </Button>
          </div>
          {loader && (!paginatedList || paginatedList.length === 0) ? (
            <div className="d-flex justify-content-center align-items-center py-5">
              <Spinner animation="border" variant="danger" />
            </div>
          ) : paginatedList && paginatedList.length > 0 ? (
            <Col>
              <Card className="qx-table-card qx-partners-table-card position-relative">
                {loader && (
                  <div
                    className="position-absolute top-0 start-0 w-100 h-100 d-flex justify-content-center align-items-center"
                    style={{ background: "rgba(255, 255, 255, 0.65)", zIndex: 10, borderRadius: "inherit" }}
                  >
                    <Spinner animation="border" variant="danger" />
                  </div>
                )}
                <Card.Body className="p-0">
                  <div
                    ref={tableScrollRef}
                    className="table-responsive"
                    onScroll={(e) => {
                      scrollPosRef.current = e.currentTarget.scrollLeft;
                    }}
                  >
                    <Table className="mb-0 align-middle">
                      <thead>
                        <tr>
                          <th>
                            <strong>S.NO</strong>
                          </th>
                          <th className="cursor-pointer" onClick={() => handleHeaderSort("hostName")}>
                            <strong>Name {sortField === "hostName" ? (sortOrder === "asc" ? "↑" : "↓") : "↕"}</strong>
                          </th>
                          <th className="cursor-pointer" onClick={() => handleHeaderSort("phoneNumber")}>
                            <strong>Phone {sortField === "phoneNumber" ? (sortOrder === "asc" ? "↑" : "↓") : "↕"}</strong>
                          </th>
                          <th className="cursor-pointer" onClick={() => handleHeaderSort("partnerData.email")}>
                            <strong>Email {sortField === "partnerData.email" ? (sortOrder === "asc" ? "↑" : "↓") : "↕"}</strong>
                          </th>
                          <th className="cursor-pointer" onClick={() => handleHeaderSort("partnerData.companyName")}>
                            <strong>Company Name {sortField === "partnerData.companyName" ? (sortOrder === "asc" ? "↑" : "↓") : "↕"}</strong>
                          </th>

                          <th className="cursor-pointer" onClick={() => handleHeaderSort("createdAt")}>
                            <strong>Created At {sortField === "createdAt" ? (sortOrder === "asc" ? "↑" : "↓") : "↕"}</strong>
                          </th>


                          <th>
                            <strong>Action</strong>
                          </th>
                        </tr>
                      </thead>
                      <tbody>
                        {paginatedList.map((partner, index) => (
                          <tr key={partner.id || index}>
                            <td>{index + 1 + currentPage * itemsPerPage}</td>
                            <td>{partner?.hostName || "-"}</td>
                            <td>{partner.countryCode} {partner.phoneNumber}</td>
                            <td>{partner?.partnerData?.email || "-"}</td>
                            <td>{partner?.partnerData?.companyName || "-"}</td>
                            <td>
                              {partner?.createdAt
                                ? new Date(partner.createdAt).toLocaleDateString("en-IN", {
                                  day: "2-digit",
                                  month: "long",
                                  year: "numeric",
                                  hour: "2-digit",
                                  minute: "2-digit",
                                  hour12: true
                                }) : ""}</td>
                            <td
                              className="qx-table-actions-cell cursor-pointer text-blue"
                              onClick={() => navigate("/create-partner", { state: { partner, activeTab, currentPage } })}
                            >
                              Create Partner
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </Table>
                  </div>
                  <AlwaysVisibleScrollbar targetRef={tableScrollRef} />
                  {partnersPagination}
                </Card.Body>
              </Card>
            </Col>
          ) : (
            <div className="qx-empty-state qx-empty-state--rich">
              <p className="qx-empty-state__title">No partners found</p>
              <p className="qx-empty-state__desc mb-0">Try adjusting your filters or search.</p>
            </div>
          )}
        </Tab>

        <Tab eventKey="active" title="Approved Partners">
          <div className="mb-3">
            <small className="qx-info-note">
              <Info size={14} />
              Partners approved and created by admin
            </small>
          </div>
          {loader && (!paginatedList || paginatedList.length === 0) ? (
            <div className="d-flex justify-content-center align-items-center py-5">
              <Spinner animation="border" variant="danger" />
            </div>
          ) : paginatedList && paginatedList.length > 0 ? (
            <Col>
              <Card className="qx-table-card qx-partners-table-card position-relative">
                {loader && (
                  <div
                    className="position-absolute top-0 start-0 w-100 h-100 d-flex justify-content-center align-items-center"
                    style={{ background: "rgba(255, 255, 255, 0.65)", zIndex: 10, borderRadius: "inherit" }}
                  >
                    <Spinner animation="border" variant="danger" />
                  </div>
                )}
                <Card.Body className="p-0">
                  <div
                    ref={tableScrollRef}
                    className="table-responsive"
                    onScroll={(e) => {
                      scrollPosRef.current = e.currentTarget.scrollLeft;
                    }}
                  >
                    <Table className="mb-0 align-middle">
                      <thead>
                        <tr>
                          <th>
                            <strong>S.NO</strong>
                          </th>
                          <th className="cursor-pointer" onClick={() => handleHeaderSort("hostName")}>
                            <strong>Name {sortField === "hostName" ? (sortOrder === "asc" ? "↑" : "↓") : "↕"}</strong>
                          </th>
                          <th className="cursor-pointer" onClick={() => handleHeaderSort("phoneNumber")}>
                            <strong>Phone {sortField === "phoneNumber" ? (sortOrder === "asc" ? "↑" : "↓") : "↕"}</strong>
                          </th>
                          <th className="cursor-pointer" onClick={() => handleHeaderSort("partnerData.email")}>
                            <strong>Email {sortField === "partnerData.email" ? (sortOrder === "asc" ? "↑" : "↓") : "↕"}</strong>
                          </th>
                          <th className="cursor-pointer" onClick={() => handleHeaderSort("partnerData.companyName")}>
                            <div><strong>Company</strong></div>
                            <small className="text-muted fw-semibold">
                              Name {sortField === "partnerData.companyName" ? (sortOrder === "asc" ? "↑" : "↓") : "↕"}
                            </small>
                          </th>
                          {renderPlanHeaders(false)}
                          <th className="cursor-pointer" onClick={() => handleHeaderSort("partnerWhatsappMessageLimit")}>
                            <div><strong>WA Message</strong></div>
                            <small className="text-muted fw-semibold">
                              Consumed {sortField === "partnerWhatsappMessageLimit" ? (sortOrder === "asc" ? "↑" : "↓") : "↕"}
                            </small>
                          </th>
                          <th className="cursor-pointer" onClick={() => handleHeaderSort("partnerRemainingWhatsappMessageLimit")}>
                            <div><strong>WA Message</strong></div>
                            <small className="text-muted fw-semibold">
                              Remaining {sortField === "partnerRemainingWhatsappMessageLimit" ? (sortOrder === "asc" ? "↑" : "↓") : "↕"}
                            </small>
                          </th>

                          <th className="cursor-pointer" onClick={() => handleHeaderSort("createdAt")}>
                            <strong>Created at {sortField === "createdAt" ? (sortOrder === "asc" ? "↑" : "↓") : "↕"}</strong>
                          </th>
                          <th className="cursor-pointer" onClick={() => handleHeaderSort("partnerData.approvedAt")}>
                            <strong>Approved At {sortField === "partnerData.approvedAt" ? (sortOrder === "asc" ? "↑" : "↓") : "↕"}</strong>
                          </th>
                          <th>
                            <strong> Action </strong>
                          </th>
                        </tr>
                      </thead>
                      <tbody>
                        {paginatedList.map((partner, index) => (
                          <tr key={partner._id || partner.id || index}>
                            <td>{index + 1 + currentPage * itemsPerPage}</td>
                            <td
                              className="cursor-pointer  "

                            >
                              {partner.hostName || "-"}
                            </td>
                            <td>{partner.countryCode || "-"} {partner.phoneNumber || "-"}</td>
                            <td>{partner?.partnerData?.email || "-"}</td>
                            <td>{partner?.partnerData?.companyName || "-"}</td>
                            {renderPlanCells(partner)}

                            <td>
                              {partner?.partnerWhatsappMessageLimit - partner?.partnerRemainingWhatsappMessageLimit || "0"}/{partner?.partnerWhatsappMessageLimit || "0"}
                            </td>

                            <td>
                              {partner?.partnerRemainingWhatsappMessageLimit || "0"}
                            </td>

                            <td>
                              {partner?.createdAt
                                ? new Date(partner.createdAt).toLocaleDateString("en-IN", {
                                  day: "2-digit",
                                  month: "long",
                                  year: "numeric",
                                  hour: "2-digit",
                                  minute: "2-digit",
                                  hour12: true
                                }) : ""}
                            </td>
                            <td>

                              {partner?.partnerData?.approvedAt
                                ? new Date(partner?.partnerData?.approvedAt).toLocaleDateString("en-IN", {
                                  day: "2-digit",
                                  month: "long",
                                  year: "numeric",
                                  hour: "2-digit",
                                  minute: "2-digit",
                                  hour12: true
                                }) : "-"}
                            </td>
                            <td className="qx-table-actions-cell">
                              <TableActionsMenu
                                items={[
                                  {
                                    label: "View Details",
                                    onClick: () =>
                                      navigate(`/partner-detail/${partner?._id}`, {
                                        state: { partner, activeTab, currentPage },
                                      }),
                                  },
                                  {
                                    label: "Assign Package",
                                    onClick: () => handleOpenAssignPackageModal(partner),
                                  },
                                  {
                                    label: "Assign WA Package",
                                    onClick: () => handleOpenAssignWaModal(partner),
                                  },
                                  {
                                    label: partner?.isBlocked ? "Enable Partner" : "Disable Partner",
                                    onClick: () => handleToggleBlockClick(partner),
                                  },
                                ]}
                              />
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </Table>
                  </div>
                  <AlwaysVisibleScrollbar targetRef={tableScrollRef} />
                  {partnersPagination}
                </Card.Body>
              </Card>
            </Col>
          ) : (
            <div className="qx-empty-state qx-empty-state--rich">
              <p className="qx-empty-state__title">No partners found</p>
              <p className="qx-empty-state__desc mb-0">Try adjusting your filters or search.</p>
            </div>
          )}
        </Tab>

        {/* <Tab eventKey="archive" title="Archive Partners">
          <div className="mb-3">
            <small className="qx-info-note">
              <Info size={14} />
              Partners that are disabled
            </small>
          </div>
          {loader ? (
            <div className="d-flex justify-content-center align-items-center py-5">
              <Spinner animation="border" variant="danger" />
            </div>
          ) : paginatedList && paginatedList.length > 0 ? (
            <Col>
              <Card className="qx-table-card qx-partners-table-card">
                <Card.Body className="p-0">
                  <Table responsive className="mb-0">
                    <thead>
                      <tr>
                        <th>
                          <strong>S.NO</strong>
                        </th>
                        <th className="cursor-pointer" onClick={() => handleHeaderSort("hostName")}>
                          <strong>Name {sortField === "hostName" ? (sortOrder === "asc" ? "↑" : "↓") : "↕"}</strong>
                        </th>
                        <th className="cursor-pointer" onClick={() => handleHeaderSort("phoneNumber")}>
                          <strong>Phone {sortField === "phoneNumber" ? (sortOrder === "asc" ? "↑" : "↓") : "↕"}</strong>
                        </th>
                        <th className="cursor-pointer" onClick={() => handleHeaderSort("partnerData.email")}>
                          <strong>Email {sortField === "partnerData.email" ? (sortOrder === "asc" ? "↑" : "↓") : "↕"}</strong>
                        </th>
                        <th className="cursor-pointer" onClick={() => handleHeaderSort("partnerData.companyName")}>
                          <div><strong>Company</strong></div>
                          <small className="text-muted fw-semibold">
                            Name {sortField === "partnerData.companyName" ? (sortOrder === "asc" ? "↑" : "↓") : "↕"}
                          </small>
                        </th>
                        <th className="cursor-pointer" onClick={() => handleHeaderSort("partnerPlans.totalHostLimit", "690310888ee7f74762970d0c")}>
                          <strong>Essential {sortField === "partnerPlans.totalHostLimit" && sortSubscriptionId === "690310888ee7f74762970d0c" ? (sortOrder === "asc" ? "↑" : "↓") : "↕"}</strong>
                        </th>
                        <th className="cursor-pointer" onClick={() => handleHeaderSort("partnerPlans.remainingHostLimit", "690310888ee7f74762970d0c")}>
                          <strong>Essential Remaining {sortField === "partnerPlans.remainingHostLimit" && sortSubscriptionId === "690310888ee7f74762970d0c" ? (sortOrder === "asc" ? "↑" : "↓") : "↕"}</strong>
                        </th>
                        <th className="cursor-pointer" onClick={() => handleHeaderSort("partnerPlans.totalHostLimit", "690310f48ee7f74762970d1d")}>
                          <strong>Premium {sortField === "partnerPlans.totalHostLimit" && sortSubscriptionId === "690310f48ee7f74762970d1d" ? (sortOrder === "asc" ? "↑" : "↓") : "↕"}</strong>
                        </th>
                        <th className="cursor-pointer" onClick={() => handleHeaderSort("partnerPlans.remainingHostLimit", "690310f48ee7f74762970d1d")}>
                          <strong>Premium Remaining {sortField === "partnerPlans.remainingHostLimit" && sortSubscriptionId === "690310f48ee7f74762970d1d" ? (sortOrder === "asc" ? "↑" : "↓") : "↕"}</strong>
                        </th>
                        <th className="cursor-pointer" onClick={() => handleHeaderSort("partnerPlans.totalHostLimit", "6903113d8ee7f74762970d24")}>
                          <strong>Elite {sortField === "partnerPlans.totalHostLimit" && sortSubscriptionId === "6903113d8ee7f74762970d24" ? (sortOrder === "asc" ? "↑" : "↓") : "↕"}</strong>
                        </th>
                        <th className="cursor-pointer" onClick={() => handleHeaderSort("partnerPlans.remainingHostLimit", "6903113d8ee7f74762970d24")}>
                          <strong>Elite Remaining {sortField === "partnerPlans.remainingHostLimit" && sortSubscriptionId === "6903113d8ee7f74762970d24" ? (sortOrder === "asc" ? "↑" : "↓") : "↕"}</strong>
                        </th>
                        <th className="cursor-pointer" onClick={() => handleHeaderSort("partnerWhatsappMessageLimit")}>
                          <div><strong>WA Message</strong></div>
                          <small className="text-muted fw-semibold">
                            Consumed {sortField === "partnerWhatsappMessageLimit" ? (sortOrder === "asc" ? "↑" : "↓") : "↕"}
                          </small>
                        </th>
                        <th className="cursor-pointer" onClick={() => handleHeaderSort("partnerRemainingWhatsappMessageLimit")}>
                          <div><strong>WA Message</strong></div>
                          <small className="text-muted fw-semibold">
                            Remaining {sortField === "partnerRemainingWhatsappMessageLimit" ? (sortOrder === "asc" ? "↑" : "↓") : "↕"}
                          </small>
                        </th>

                        <th className="cursor-pointer" onClick={() => handleHeaderSort("createdAt")}>
                          <strong>Created at {sortField === "createdAt" ? (sortOrder === "asc" ? "↑" : "↓") : "↕"}</strong>
                        </th>
                        <th className="cursor-pointer" onClick={() => handleHeaderSort("partnerData.approvedAt")}>
                          <strong>Approved At {sortField === "partnerData.approvedAt" ? (sortOrder === "asc" ? "↑" : "↓") : "↕"}</strong>
                        </th>
                        <th>
                          <strong> Action </strong>
                        </th>
                      </tr>
                    </thead>
                    <tbody>
                      {paginatedList.map((partner, index) => (
                        <tr key={partner._id || partner.id || index}>
                          <td>{index + 1 + currentPage * itemsPerPage}</td>
                          <td>{partner.hostName || "-"}</td>
                          <td>{partner?.countryCode || "-"} {partner?.phoneNumber || "-"}</td>
                          <td>{partner?.partnerData?.email || "-"}</td>
                          <td>{partner?.partnerData?.companyName || "-"}</td>
                          <td>
                            {partner.essentialPlan
                              ? `${(partner.essentialPlan.totalHostLimit ?? partner.essentialPlan.hostLimit ?? 0) - (partner.essentialPlan.remainingHostLimit ?? 0)} / ${partner.essentialPlan.totalHostLimit ?? partner.essentialPlan.hostLimit ?? "-"}`
                              : "-"}
                          </td>
                          <td>
                            {partner.essentialPlan
                              ? (partner.essentialPlan.remainingHostLimit ?? "-")
                              : "-"}
                          </td>
                          <td>
                            {partner.premiumPlan
                              ? `${(partner.premiumPlan.totalHostLimit ?? partner.premiumPlan.hostLimit ?? 0) - (partner.premiumPlan.remainingHostLimit ?? 0)} / ${partner.premiumPlan.totalHostLimit ?? partner.premiumPlan.hostLimit ?? "-"}`
                              : "-"}
                          </td>
                          <td>
                            {partner.premiumPlan
                              ? (partner.premiumPlan.remainingHostLimit ?? "-")
                              : "-"}
                          </td>
                          <td>
                            {partner.elitePlan
                              ? `${(partner.elitePlan.totalHostLimit ?? partner.elitePlan.hostLimit ?? 0) - (partner.elitePlan.remainingHostLimit ?? 0)} / ${partner.elitePlan.totalHostLimit ?? partner.elitePlan.hostLimit ?? "-"}`
                              : "-"}
                          </td>
                          <td>
                            {partner.elitePlan
                              ? (partner.elitePlan.remainingHostLimit ?? "-")
                              : "-"}
                          </td>

                          <td>
                            {partner?.partnerWhatsappMessageLimit - partner?.partnerRemainingWhatsappMessageLimit || "0"}/{partner?.partnerWhatsappMessageLimit || "0"}
                          </td>

                          <td>
                            {partner?.partnerRemainingWhatsappMessageLimit || "0"}
                          </td>

                          <td>
                            {partner?.createdAt
                              ? new Date(partner.createdAt).toLocaleDateString("en-IN", {
                                day: "2-digit",
                                month: "long",
                                year: "numeric",
                                hour: "2-digit",
                                minute: "2-digit",
                                hour12: true,
                              })
                              : ""}
                          </td>
                          <td>
                            {partner?.partnerData?.approvedAt
                              ? new Date(partner?.partnerData?.approvedAt).toLocaleDateString("en-IN", {
                                day: "2-digit",
                                month: "long",
                                year: "numeric",
                                hour: "2-digit",
                                minute: "2-digit",
                                hour12: true,
                              })
                              : "-"}
                          </td>
                          <td className="qx-table-actions-cell">
                            <TableActionsMenu
                              items={[
                                {
                                  label: "View Details",
                                  onClick: () =>
                                    navigate(`/partner-detail/${partner?._id}`, {
                                      state: { partner, activeTab, currentPage },
                                    }),
                                },
                                {
                                  label: partner?.isBlocked ? "Enable Partner" : "Disable Partner",
                                  onClick: () => handleToggleBlockClick(partner),
                                },
                                {
                                  label: "Delete",
                                  danger: true,
                                  onClick: () => {
                                    setDeletePartnerId(partner._id || partner.id);
                                    setShowDeleteModal(true);
                                  },
                                },
                              ]}
                            />
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </Table>
                  {partnersPagination}
                </Card.Body>
              </Card>
            </Col>
          ) : (
            <div className="qx-empty-state qx-empty-state--rich">
              <p className="qx-empty-state__title">No partners found</p>
              <p className="qx-empty-state__desc mb-0">Try adjusting your filters or search.</p>
            </div>
          )}
        </Tab> */}

        <Tab eventKey="inactive" title="At-Risk Partners">
          <div className="mb-3 d-flex align-items-center justify-content-between flex-wrap gap-2">
            <small className="qx-info-note mb-0">
              <Info size={14} />
              {inactiveType === "noWedding"
                ? "Partners with no wedding created in the last 30 days"
                : inactiveType === "noLogin"
                  ? "Partners with no login in the last 30 days"
                  : "All at-risk partners"}
            </small>

            <div className="d-flex align-items-center gap-2">
              <span className="small fw-bold text-muted">Filter:</span>
              <Form.Select
                size="sm"
                value={inactiveType}
                onChange={(e) => setInactiveType(e.target.value)}
                className="fw-bold"
                style={{ width: "160px", borderRadius: "0.5rem", borderColor: "#A60000", color: "#800001" }}
              >
                <option value="noLogin">No Login</option>
                <option value="noWedding">No Wedding Created</option>
              </Form.Select>
            </div>
          </div>
          {loader && (!paginatedList || paginatedList.length === 0) ? (
            <div className="d-flex justify-content-center align-items-center py-5">
              <Spinner animation="border" variant="danger" />
            </div>
          ) : paginatedList && paginatedList.length > 0 ? (
            <Col>
              <Card className="qx-table-card qx-partners-table-card position-relative">
                {loader && (
                  <div
                    className="position-absolute top-0 start-0 w-100 h-100 d-flex justify-content-center align-items-center"
                    style={{ background: "rgba(255, 255, 255, 0.65)", zIndex: 10, borderRadius: "inherit" }}
                  >
                    <Spinner animation="border" variant="danger" />
                  </div>
                )}
                <Card.Body className="p-0">
                  <div
                    ref={tableScrollRef}
                    className="table-responsive"
                    onScroll={(e) => {
                      scrollPosRef.current = e.currentTarget.scrollLeft;
                    }}
                  >
                    <Table className="mb-0 align-middle">
                      <thead>
                        <tr>
                          <th>
                            <strong>S.NO</strong>
                          </th>
                          <th className="cursor-pointer" onClick={() => handleHeaderSort("hostName")}>
                            <strong>Name {sortField === "hostName" ? (sortOrder === "asc" ? "↑" : "↓") : "↕"}</strong>
                          </th>
                          <th className="cursor-pointer" onClick={() => handleHeaderSort("phoneNumber")}>
                            <strong>Phone {sortField === "phoneNumber" ? (sortOrder === "asc" ? "↑" : "↓") : "↕"}</strong>
                          </th>
                          <th className="cursor-pointer" onClick={() => handleHeaderSort("partnerData.email")}>
                            <strong>Email {sortField === "partnerData.email" ? (sortOrder === "asc" ? "↑" : "↓") : "↕"}</strong>
                          </th>

                          <th className="cursor-pointer" onClick={() => handleHeaderSort("partnerData.companyName")}>
                            <div><strong>Company</strong></div>
                            <small className="text-muted fw-semibold">
                              Name {sortField === "partnerData.companyName" ? (sortOrder === "asc" ? "↑" : "↓") : "↕"}
                            </small>
                          </th>
                          <th className="cursor-pointer" onClick={() => handleHeaderSort("lastApiCallAt")}>
                            <strong>Last Active at {sortField === "lastApiCallAt" ? (sortOrder === "asc" ? "↑" : "↓") : "↕"}</strong>
                          </th>
                          {renderPlanHeaders(true)}
                          <th className="cursor-pointer" onClick={() => handleHeaderSort("partnerWhatsappMessageLimit")}>
                            <div><strong>WA Message</strong></div>
                            <small className="text-muted fw-semibold">
                              Consumed {sortField === "partnerWhatsappMessageLimit" ? (sortOrder === "asc" ? "↑" : "↓") : "↕"}
                            </small>
                          </th>
                          <th className="cursor-pointer" onClick={() => handleHeaderSort("partnerRemainingWhatsappMessageLimit")}>
                            <div><strong>WA Message</strong></div>
                            <small className="text-muted fw-semibold">
                              Remaining {sortField === "partnerRemainingWhatsappMessageLimit" ? (sortOrder === "asc" ? "↑" : "↓") : "↕"}
                            </small>
                          </th>
                          <th className="cursor-pointer" onClick={() => handleHeaderSort("createdAt")}>
                            <strong>Created at {sortField === "createdAt" ? (sortOrder === "asc" ? "↑" : "↓") : "↕"}</strong>
                          </th>


                          <th>
                            <strong> Action </strong>
                          </th>
                        </tr>
                      </thead>
                      <tbody>
                        {paginatedList.map((partner, index) => (
                          <tr key={partner._id || partner.id || index}>
                            <td>{index + 1 + currentPage * itemsPerPage}</td>
                            <td>{partner.hostName || "-"}</td>
                            <td>{partner?.countryCode || "-"} {partner?.phoneNumber || "-"}</td>
                            <td>{partner?.partnerData?.email || "-"}</td>
                            <td>{partner?.partnerData?.companyName || "-"}</td>
                            <td>
                              {moment.utc(partner?.lastApiCallAt).local().format("DD MMM YYYY, hh:mm A") || "0"}
                            </td>
                            {renderPlanCells(partner)}

                            <td>
                              {partner?.partnerWhatsappMessageLimit - partner?.partnerRemainingWhatsappMessageLimit || "0"}/{partner?.partnerWhatsappMessageLimit || "0"}
                            </td>

                            <td>
                              {partner?.partnerRemainingWhatsappMessageLimit || "0"}
                            </td>

                            <td>
                              {partner?.createdAt
                                ? new Date(partner.createdAt).toLocaleDateString("en-IN", {
                                  day: "2-digit",
                                  month: "long",
                                  year: "numeric",
                                  hour: "2-digit",
                                  minute: "2-digit",
                                  hour12: true,
                                })
                                : ""}
                            </td>




                            <td className="qx-table-actions-cell">
                              <TableActionsMenu
                                items={[
                                  {
                                    label: "View Details",
                                    onClick: () =>
                                      navigate(`/partner-detail/${partner?._id}`, {
                                        state: { partner, activeTab, currentPage },
                                      }),
                                  },
                                  {
                                    label: partner?.isBlocked ? "Enable Partner" : "Disable Partner",
                                    onClick: () => handleToggleBlockClick(partner),
                                  },
                                  // {
                                  //   label: "Delete",
                                  //   danger: true,
                                  //   onClick: () => {
                                  //     setDeletePartnerId(partner._id || partner.id);
                                  //     setShowDeleteModal(true);
                                  //   },
                                  // },
                                ]}
                              />
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </Table>
                  </div>
                  <AlwaysVisibleScrollbar targetRef={tableScrollRef} />
                  {partnersPagination}
                </Card.Body>
              </Card>
            </Col>
          ) : (
            <div className="qx-empty-state qx-empty-state--rich">
              <p className="qx-empty-state__title">No partners found</p>
              <p className="qx-empty-state__desc mb-0">Try adjusting your filters or search.</p>
            </div>
          )}
        </Tab>
      </Tabs>

      {/* Enable / Disable Partner Confirmation Modal */}
      <Modal show={showBlockModal} onHide={() => setShowBlockModal(false)} centered>
        <Modal.Header closeButton>
          <Modal.Title style={{ color: "#A60000" }}>
            {targetPartner?.isBlocked ? "Enable Partner" : "Disable Partner"}
          </Modal.Title>
        </Modal.Header>
        <Modal.Body>
          Are you sure you want to {targetPartner?.isBlocked ? "enable" : "disable"}{" "}
          <strong>{targetPartner?.hostName || targetPartner?.partnerData?.companyName || "this partner"}</strong>?
        </Modal.Body>
        <Modal.Footer>
          <Button variant="secondary" onClick={() => setShowBlockModal(false)} disabled={blockLoading}>
            Cancel
          </Button>
          <Button

            onClick={handleConfirmToggleBlock}
            disabled={blockLoading}
          >
            {blockLoading ? (
              <Spinner animation="border" size="sm" />
            ) : targetPartner?.isBlocked ? (
              "Yes, Enable"
            ) : (
              "Yes, Disable"
            )}
          </Button>
        </Modal.Footer>
      </Modal>

      <ConfirmDeleteModal
        show={showDeleteModal}
        onHide={() => {
          setShowDeleteModal(false);
          setDeletePartnerId(null);
        }}
        onConfirm={handleDeletePartner}
        title="Delete Partner"
        message="Are you sure you want to delete this active partner? This action cannot be undone."
      />

      {/* Assign Package Modal */}
      <Modal show={showPackageModal} onHide={() => setShowPackageModal(false)} centered>
        <Modal.Header closeButton className="border-0 pb-0">
          <Modal.Title className="fw-bold h5" style={{ color: "#800001" }}>
            Assign Package
          </Modal.Title>
        </Modal.Header>
        <Modal.Body className="py-3">
          {assignTargetPartner && (
            <div style={{ background: "#A60000", color: "#FFB101" }} className="mb-3 p-2  rounded  small">
              Assigning package for partner: <strong className="text-light">{assignTargetPartner.hostName || assignTargetPartner.name || "Partner"}</strong>
            </div>
          )}

          {/* Package Type selection commented out - keeping Individual Package only */}
          {/* <Form.Group className="mb-3">
            <Form.Label className="fw-semibold text-secondary small text-uppercase mb-2 d-block">
              Select Package Type *
            </Form.Label>
            <div className="d-flex gap-2">
              <Button
                type="button"
                size="sm"
                className="px-3 py-2 fw-semibold"
                style={{
                  borderRadius: "0.5rem",
                  backgroundColor: assignPackageType === "individual" ? "#A60000" : "#ffffff",
                  borderColor: assignPackageType === "individual" ? "#800001" : "#E2E8F0",
                  color: assignPackageType === "individual" ? "#ffffff" : "#4a5568",
                }}
                onClick={() => {
                  setAssignPackageType("individual");
                  setSelectedAssignPackageId("");
                }}
              >
                Individual Package
              </Button>
              <Button
                type="button"
                size="sm"
                className="px-3 py-2 fw-semibold"
                style={{
                  borderRadius: "0.5rem",
                  backgroundColor: assignPackageType === "combined" ? "#A60000" : "#ffffff",
                  borderColor: assignPackageType === "combined" ? "#800001" : "#E2E8F0",
                  color: assignPackageType === "combined" ? "#ffffff" : "#4a5568",
                }}
                onClick={() => {
                  setAssignPackageType("combined");
                  setSelectedAssignPackageId("");
                }}
              >
                Combined Package
              </Button>
            </div>
          </Form.Group> */}

          <Form.Group controlId="assignPackageSelect" className="mb-3">
            <Form.Label className="fw-semibold text-secondary small text-uppercase mb-1">
              Select Package *
            </Form.Label>
            <Form.Select
              value={selectedAssignPackageId}
              onChange={(e) => {
                const newId = e.target.value;
                setSelectedAssignPackageId(newId);
                const pkgObj = individualPackages.find((pkg) => (pkg._id || pkg.id) === newId);
                if (pkgObj?.price !== undefined && pkgObj?.price !== null) {
                  setReceivedMoney(String(pkgObj.price));
                }
              }}
              style={{ borderRadius: "0.5rem", borderColor: "#A60000", height: "44px" }}
            >
              <option value="">Select Package</option>
              {individualPackages.map((pkg) => (
                <option key={pkg._id || pkg.id} value={pkg._id || pkg.id}>
                  {pkg.name || pkg.title} {pkg.price ? `(₹${pkg.price})` : ""}
                </option>
              ))}
            </Form.Select>
          </Form.Group>

          {/* Actual Amount (Readonly) */}
          <Form.Group className="mb-3">
            <Form.Label className="fw-semibold text-secondary small text-uppercase mb-1">
              Actual Amount
            </Form.Label>
            <Form.Control
              type="text"
              readOnly
              disabled
              value={selectedPackageObj?.price ? `₹ ${selectedPackageObj.price}` : "-"}
              style={{
                borderRadius: "0.5rem",
                height: "44px",
                backgroundColor: "#f1f5f9",
                color: "#64748b",
                fontWeight: "600",
              }}
            />
          </Form.Group>

          {/* Received Money (Editable) */}
          <Form.Group className="mb-3">
            <Form.Label className="fw-semibold text-secondary small text-uppercase mb-1">
              Received Money *
            </Form.Label>
            <div style={{ position: "relative" }}>

              <Form.Control
                type="text"
                inputMode="decimal"
                placeholder="Enter received amount"
                value={receivedMoney}
                onChange={(e) => {
                  let value = e.target.value.replace(/[^0-9.]/g, "");
                  const parts = value.split(".");
                  if (parts.length > 2) {
                    value = parts[0] + "." + parts.slice(1).join("");
                  }
                  setReceivedMoney(value);
                }}
                style={{
                  borderRadius: "0.5rem",
                  borderColor: "#A60000",
                  height: "44px",
                  paddingLeft: "30px",
                }}
              />
            </div>
          </Form.Group>
        </Modal.Body>
        <Modal.Footer className="border-0 pt-0">
          <Button variant="light" size="sm" onClick={() => setShowPackageModal(false)} disabled={assignPackageLoading}>
            Cancel
          </Button>
          <Button
            size="sm"
            onClick={handleConfirmAssignPackage}
            disabled={assignPackageLoading}
            style={{ backgroundColor: "#A60000", borderColor: "#A60000", color: "#FFB101" }}
          >
            {assignPackageLoading ? <Spinner animation="border" size="sm" /> : "Assign Package"}
          </Button>
        </Modal.Footer>
      </Modal>

      {/* Assign WhatsApp Package Modal */}
      <Modal show={showWaModal} onHide={() => setShowWaModal(false)} centered>
        <Modal.Header closeButton className="border-0 pb-0">
          <Modal.Title className="fw-bold h5" style={{ color: "#800001" }}>
            Assign WhatsApp Package
          </Modal.Title>
        </Modal.Header>
        <Modal.Body className="py-3">
          {assignWaPartner && (
            <div style={{ background: "#A60000", color: "#FFB101" }} className="mb-3 p-2 rounded small">
              Assigning WhatsApp package for partner: <strong className="text-light">{assignWaPartner.hostName || assignWaPartner?.partnerData?.companyName || "Partner"}</strong>
            </div>
          )}

          <Form.Group className="mb-3">
            <Form.Label className="fw-semibold text-secondary small text-uppercase mb-1">
              Select WhatsApp Package *
            </Form.Label>
            <Form.Select
              value={selectedWaPlanId}
              onChange={(e) => {
                const val = e.target.value;
                setSelectedWaPlanId(val);
                const selectedPkg = whatsAppPackagesList.find((pkg) => (pkg._id || pkg.id) === val);
                if (selectedPkg && selectedPkg.price !== undefined) {
                  setWaReceivedMoney(String(selectedPkg.price));
                }
              }}
              style={{ borderRadius: "0.5rem", borderColor: "#A60000", height: "44px" }}
            >
              <option value="">-- Choose WhatsApp Package --</option>
              {whatsAppPackagesList.map((pkg) => (
                <option key={pkg._id || pkg.id} value={pkg._id || pkg.id}>
                  {pkg.name} — ₹{pkg.price || 0} ({(pkg.messagesLimit ?? pkg.messageLimit ?? pkg.whatsappMessageLimit ?? 0).toLocaleString("en-IN")} Messages)
                </option>
              ))}
            </Form.Select>
          </Form.Group>

          {selectedWaPlanId && (() => {
            const pkg = whatsAppPackagesList.find((p) => (p._id || p.id) === selectedWaPlanId);
            if (!pkg) return null;
            return (
              <div className="mb-3 p-3 rounded border" style={{ backgroundColor: "#fff5f5", borderColor: "#A60000" }}>
                <div className="fw-bold mb-1" style={{ color: "#800001" }}>{pkg.name}</div>
                <div className="small text-muted mb-1">
                  💬 Messages: <strong>{(pkg.messagesLimit ?? pkg.messageLimit ?? pkg.whatsappMessageLimit ?? 0).toLocaleString("en-IN")}</strong>
                </div>
                <div className="small text-muted">
                  💰 Standard Price: <strong>₹{pkg.price || 0}</strong>
                </div>
              </div>
            );
          })()}

          {/* Received Money (Editable) */}
          <Form.Group className="mb-3">
            <Form.Label className="fw-semibold text-secondary small text-uppercase mb-1">
              Received Money *
            </Form.Label>
            <div style={{ position: "relative" }}>

              <Form.Control
                type="text"
                inputMode="decimal"
                placeholder="Enter received amount"
                value={waReceivedMoney}
                onChange={(e) => {
                  let value = e.target.value.replace(/[^0-9.]/g, "");
                  const parts = value.split(".");
                  if (parts.length > 2) {
                    value = parts[0] + "." + parts.slice(1).join("");
                  }
                  setWaReceivedMoney(value);
                }}
                style={{
                  borderRadius: "0.5rem",
                  borderColor: "#A60000",
                  height: "44px",
                  paddingLeft: "30px",
                }}
              />
            </div>
          </Form.Group>
        </Modal.Body>
        <Modal.Footer className="border-0 pt-0">
          <Button variant="light" size="sm" onClick={() => setShowWaModal(false)} disabled={assignWaLoading}>
            Cancel
          </Button>
          <Button
            size="sm"
            onClick={handleConfirmAssignWaPackage}
            disabled={assignWaLoading}
            style={{ backgroundColor: "#A60000", borderColor: "#A60000", color: "#FFB101" }}
          >
            {assignWaLoading ? <Spinner animation="border" size="sm" /> : "Assign WA Package"}
          </Button>
        </Modal.Footer>
      </Modal>
    </div>
  );
}
